import { Tooltip } from "antd";
import React from "react";
import useCurrency from "../../../../../../utils/hooks/useCurrency";
import { ChantInput } from "../../../../../../CommonComponents";
import ChantSelect from "../../../../../../CommonComponents/ChantSelect/ChantSelect";
import "./TicketItem.css";

const TicketItem = ({
  index,
  ticketDetail,
  handleTicketDetailInputChange,
  handleTicketDetailOptionChange,
  handleTicketDetailDelete,
  soldTickets,
  ticketType,
  ticketTypes,
}) => {
  const groupCurrency = useCurrency();

  const nameEmailOptions = [
    "Not required",
    "Require name",
    "Require name & email",
  ];

  return (
    <div className="ticket-item-container" key={index}>
      <div className="ticket-item-card">
        <div className="ticket-item-header">
          <h3 className="ticket-item-title">
            Item #{index + 1}
            <Tooltip
              placement="topLeft"
              title={
                <div className="ticket-item-tooltip">
                  <p>
                    A Group Ticket can have multiple ticket options. Quantities
                    are set separately for each option so do not offer a limited
                    quantity ticket across two options. Each option can have
                    it&apos;s own limit per member, offer a barcode (except
                    match tickets) and require name/email.
                  </p>
                  <p>
                    Examples: Food Drink Food and Drink Tailgate and Parking
                  </p>
                </div>
              }
            >
              <i className="fa fa-question-circle-o ticket-item-help-icon" />
            </Tooltip>
          </h3>
          <span
            className="ticket-item-delete"
            onClick={handleTicketDetailDelete}
          >
            Delete
          </span>
        </div>

        <div className="ticket-item-form">
          <div className="ticket-item-form-group">
            <ChantInput
              label="Ticket"
              value={ticketDetail.name}
              type="text"
              onChange={(e) => handleTicketDetailInputChange(e, index)}
              name="name"
              placeholder={
                ticketType === "Group Match"
                  ? "Ex. Match ticket or Match + Bus"
                  : "Ex. Food & Drink"
              }
              required
              maxLength={25}
            />
          </div>
          <div className="ticket-item-form-group">
            <div style={{ marginBottom: "6px", display: "flex", alignItems: "center", gap: "4px" }}>
              <span style={{ fontWeight: 500 }}>Quantity <span style={{ color: "red" }}>*</span></span>
              <Tooltip title={<div className="ticket-item-tooltip">Set number of available tickets for sale.</div>}>
                <i className="fa fa-question-circle-o" />
              </Tooltip>
            </div>
            <ChantInput
              value={ticketDetail.quantity}
              type="number"
              onChange={({ target }) => {
                let { name, value } = target;
                value = Number(value).toFixed(0);
                handleTicketDetailInputChange({ target: { name, value } }, index);
              }}
              name="quantity"
              placeholder="0"
              min={soldTickets !== 0 ? soldTickets : 1}
            />
          </div>
          <div className="ticket-item-form-group">
            <div style={{ marginBottom: "6px", display: "flex", alignItems: "center", gap: "4px" }}>
              <span style={{ fontWeight: 500 }}>Member Price({groupCurrency}) <span style={{ color: "red" }}>*</span></span>
              <Tooltip title={<div className="ticket-item-tooltip">Enter price per ticket for a member. If ticket is free enter &apos;0&apos; and member can &apos;Reserve&apos; tickets</div>}>
                <i className="fa fa-question-circle-o" />
              </Tooltip>
            </div>
            <ChantInput
              value={ticketDetail.memberPrice}
              type="number"
              onChange={({ target }) => {
                let { name, value } = target;
                value = value.indexOf(".") > 0 ? value.substr(0, value.indexOf(".") + 3) : value;
                handleTicketDetailInputChange({ target: { name, value } }, index);
              }}
              name="memberPrice"
              step="0.01"
              placeholder="0"
              min={0}
            />
          </div>
          <div className="ticket-item-form-group">
            <div style={{ marginBottom: "6px", display: "flex", alignItems: "center", gap: "4px" }}>
              <span style={{ fontWeight: 500 }}>Non member price</span>
              <Tooltip title="Enter price per ticket for a member for a non-member. If ticket is free enter '0' and member can 'Reserve' tickets">
                <i className="fa fa-question-circle-o" />
              </Tooltip>
            </div>
            <ChantInput
              value={ticketDetail.nonMemberPrice}
              type="number"
              onChange={({ target }) => {
                let { name, value } = target;
                value = value.indexOf(".") > 0 ? value.substr(0, value.indexOf(".") + 3) : value;
                handleTicketDetailInputChange({ target: { name, value } }, index);
              }}
              name="nonMemberPrice"
              step="0.01"
              placeholder="0"
              min={0}
            />
          </div>
          <div className="ticket-item-form-group">
            <div style={{ marginBottom: "6px", display: "flex", alignItems: "center", gap: "4px" }}>
              <span style={{ fontWeight: 500 }}>Limit per person <span style={{ color: "red" }}>*</span></span>
              <Tooltip
                title={
                  ticketType !== "Tailgate Season Pass" ? (
                    <div className="ticket-item-tooltip">
                      If no ticket limit, select N/A. Note that limit per person
                      can not be controlled on the web -- guests can purchase the
                      max, then purchase again. Web tickets should not be used for
                      tickets where a max quantity is important, or should be
                      first offered to members.
                    </div>
                  ) : (
                    <div className="ticket-item-tooltip">
                      Set number of tickets in pass. This value will not be
                      editable for member.
                    </div>
                  )
                }
              >
                <i className="fa fa-question-circle-o" />
              </Tooltip>
            </div>
            <ChantSelect
              value={ticketDetail.limitPerPerson || undefined}
              onChange={(value) => handleTicketDetailOptionChange({ target: { name: "limitPerPerson", value } }, index)}
              name="limitPerPerson"
              options={(ticketDetail.ticketLimit || []).map((limit) =>
                Number(limit) === 0
                  ? { value: (ticketDetail.ticketLimit.length || 1) - 1, label: "NA" }
                  : { value: limit, label: String(limit) }
              )}
              placeholder="--Select Ticket Limit--"
            />
          </div>
          <div className="ticket-item-form-group">
            <div style={{ marginBottom: "6px", display: "flex", alignItems: "center", gap: "4px" }}>
              <span style={{ fontWeight: 500 }}>Subticket Type <span style={{ color: "red" }}>*</span></span>
              <Tooltip
                title={
                  <div className="ticket-item-tooltip">
                    <p>Each ticket type provides the option of requiring name or name/email.</p>
                    <p>Match tickets do not allow a QR code as the actual ticket will be distributed by the group outside of Chant. This avoids member confusion. Manual check-in (in the ticket check-in section) is available to the admin.</p>
                    <p>Other ticket types do allow a QR code to be displayed providing groups the option of scanning tickets upon event check-in. Only add QR codes for tickets you control.</p>
                  </div>
                }
              >
                <i className="fa fa-question-circle-o" />
              </Tooltip>
            </div>
            <ChantSelect
              value={ticketDetail.subTicketType || undefined}
              onChange={(value) => handleTicketDetailOptionChange({ target: { name: "subTicketType", value } }, index)}
              name="subTicketType"
              options={(ticketTypes || []).map((t) => ({ value: t, label: t }))}
              placeholder="--Select--"
            />
          </div>
          {ticketDetail.subTicketType && (
            <div className="ticket-item-form-group">
              <ChantSelect
                label="Require name/email per ticket"
                required
                value={ticketDetail.requireNameEmail || undefined}
                onChange={(value) => handleTicketDetailOptionChange({ target: { name: "requireNameEmail", value } }, index)}
                name="requireNameEmail"
                options={nameEmailOptions.map((o) => ({ value: o, label: o }))}
                placeholder="--Select--"
              />
            </div>
          )}
          {ticketDetail.subTicketType &&
            ticketDetail.subTicketType.length > 0 &&
            ticketDetail.subTicketType !== "Group Match" && (
              <div className="ticket-item-form-group">
                <div style={{ marginBottom: "6px", display: "flex", alignItems: "center", gap: "4px" }}>
                  <span style={{ fontWeight: 500 }}>Enable check-in? <span style={{ color: "red" }}>*</span></span>
                  <Tooltip
                    title={
                      <div className="ticket-item-tooltip">
                        <p>Only add QR codes for tickets you control (tailgate, bus, special event, etc). Do not show for match tickets as those will be distributed by you outside of Chant.</p>
                        <p>Chant provides the option to have each non-match ticket appear with a QR code. Group leaders (and ticket scanner role) can scan tickets by clicking on the QR scanner icon associated with the ticket they intend to scan.</p>
                        <p>QR codes change from green to red when scanned.</p>
                      </div>
                    }
                  >
                    <i className="fa fa-question-circle-o" />
                  </Tooltip>
                </div>
                <ChantSelect
                  value={ticketDetail.generateQRCode || undefined}
                  onChange={(value) => handleTicketDetailOptionChange({ target: { name: "generateQRCode", value } }, index)}
                  name="generateQRCode"
                  options={[{ value: "Yes", label: "Yes" }, { value: "No", label: "No" }]}
                  placeholder="--Select--"
                />
                <div style={{ fontSize: "12px", color: "#888", marginTop: "4px" }}>
                  QR codes are not generated for web tickets. Use receipt for entry.
                </div>
              </div>
            )}
        </div>
      </div>
    </div>
  );
};

export default TicketItem;
